import { ReactNode } from 'react'

import { RowSelection } from '../hooks/useGridRowSelection'
import { Id } from '../types'
import { SummaryCell } from './SummaryCell'
import { TableCell } from './TableCell'

type BodyRowProps<TCol extends { id: Id }, TRow extends { id: Id }> = {
    cols: TCol[]

    row: TRow
    selection: RowSelection
    cursorColId: string | undefined
    rowHeader?: (row: TRow) => ReactNode
    rowClassName?: (row: TRow) => string

    children: (row: TRow, col: TCol, isSelected: boolean) => ReactNode
    cellClassName?: (row: TRow, col: TCol) => string
    cellDataTest?: string
    height?: number
    gridTemplateColumns: string
    summaryCellRender?: (row: TRow) => ReactNode
}

type BodyCellProps<TCol extends { id: Id }, TRow extends { id: Id }> = Omit<
    BodyRowProps<TCol, TRow>,
    'cols' | 'rowHeader' | 'rowClassName' | 'gridTemplateColumns' | 'summaryCellRender'
> & { col: TCol }

function BodyCell<TCol extends { id: Id }, TRow extends { id: Id }>({
    row,
    col,
    selection,
    cursorColId,
    cellClassName,
    children,
    height,
}: BodyCellProps<TCol, TRow>) {
    const colId = String(col.id)
    const isSelected = selection.has(colId)
    const selectionBorders = selection.get(colId) ?? ''
    const isCursor = cursorColId === colId
    const isRowSelected = selection.size !== 0
    return (
        <TableCell
            row={row}
            col={col}
            isCursor={isCursor}
            isSelected={isSelected}
            isRowSelected={isRowSelected}
            selectionBorders={selectionBorders}
            cellClassName={cellClassName}
            height={height}
        >
            {children}
        </TableCell>
    )
}

export function BodyRow<TCol extends { id: Id }, TRow extends { id: Id }>({
    cols,
    row,
    selection,
    cursorColId,
    height,
    rowHeader,
    children,
    rowClassName,
    cellClassName,
    gridTemplateColumns,
    summaryCellRender,
}: BodyRowProps<TCol, TRow>) {
    const isRowSelected = selection.size !== 0
    return (
        <div id={`row-${row.id}`} className={`group grid ${rowClassName?.(row)}`} style={{ gridTemplateColumns }} data-test="body-row">
            {rowHeader && (
                <div
                    style={{ height }}
                    role="rowheader"
                    className={`${isRowSelected ? '!bg-surface-strong' : ''} sticky left-0 z-elevated flex items-center truncate border-b border-r-2 bg-surface-base`}
                >
                    {rowHeader(row)}
                </div>
            )}

            {cols.map(col => (
                <BodyCell key={col.id} col={col} row={row} height={height} selection={selection} cursorColId={cursorColId} cellClassName={cellClassName}>
                    {children}
                </BodyCell>
            ))}
            {summaryCellRender && (
                <SummaryCell style={{ height }} className={`flex items-center justify-center border-b p-1 ${isRowSelected ? '!bg-surface-strong' : ''}`}>
                    {summaryCellRender(row)}
                </SummaryCell>
            )}
        </div>
    )
}
